import { useState } from 'react';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { Banner, Button, assets } from '@round/whitelabel-ui';
import { asMoney, showNotification } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useInvalidateCreatorSearch, useUpdateClientInfluencer } from '@round/creatorbase-helpers/api-hooks';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { isCreatorCostMismatched } from './helpers';
import styles from './CreatorCostMismatchBanner.module.css';

const { TriangleExclamation: WarningIcon } = assets;

type Props = {
    clientInfluencerId: string | null | undefined;
    platform: Extract<creatorbase.Platform, 'tiktok' | 'instagram'>;
    creatorCost: number | null;
    creatorCurrencyId: number | null;
    paymentCost: number | null | undefined;
    paymentCurrencyId: number | null | undefined;
    className?: string;
};

const CreatorCostMismatchBanner = ({
    clientInfluencerId,
    platform,
    creatorCost,
    creatorCurrencyId,
    paymentCost,
    paymentCurrencyId,
    className,
}: Props) => {
    const { getCurrencyById } = useCurrencies();
    const queryClient = useQueryClient();
    const invalidateCreatorSearch = useInvalidateCreatorSearch();
    const detailKey = creatorbaseQueries.clientInfluencer.keys.detail(clientInfluencerId ?? '');

    // Keyed by the mismatched payment, so dismissing one mismatch doesn't hide a later, different one.
    const [dismissedMismatchKey, setDismissedMismatchKey] = useState<string | null>(null);

    // Patches on success rather than optimistically, so the spinner shows for the request's full duration.
    const { update, isLoading } = useUpdateClientInfluencer(clientInfluencerId, {
        onSuccess: (data) => {
            queryClient.setQueryData<creatorbase.ClientInfluencer>(detailKey, (current) =>
                current ? { ...current, ...data } : current
            );
            showNotification('Creator cost updated', 'info');
        },
        onError: () => {
            showNotification('Could not update creator cost', 'error');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: detailKey });
            // The creator's cost is also denormalized onto creator explore search results (used to
            // prefill PostForm), so those caches go stale too and need their own invalidation.
            invalidateCreatorSearch();
        },
    });

    const isMismatched = isCreatorCostMismatched(creatorCost, creatorCurrencyId, paymentCost, paymentCurrencyId);
    // Includes every value shown in the banner's comparison, so dismissing a mismatch doesn't hide a
    // later mismatch - for a different creator/platform, or the same one with a changed creator cost -
    // that happens to share the same payment cost and currency.
    const mismatchKey = `${clientInfluencerId}:${platform}:${creatorCost}:${creatorCurrencyId}:${paymentCost}:${paymentCurrencyId}`;

    if (
        !isMismatched ||
        !clientInfluencerId ||
        paymentCost === null ||
        paymentCost === undefined ||
        !paymentCurrencyId ||
        dismissedMismatchKey === mismatchKey
    ) {
        return null;
    }

    const hasCreatorCost = creatorCost !== null && creatorCurrencyId !== null;
    const creatorAmount = asMoney(creatorCost, getCurrencyById(creatorCurrencyId));
    const paymentAmount = asMoney(paymentCost, getCurrencyById(paymentCurrencyId));
    const platformLabel = platform === 'tiktok' ? 'TikTok' : 'Instagram';

    const handleUpdate = () => {
        const patchData: Partial<creatorbase.PatchClientInfluencerData> & { id: string } =
            platform === 'tiktok'
                ? { id: clientInfluencerId, tiktok_cost: paymentCost, tiktok_currency_id: paymentCurrencyId }
                : { id: clientInfluencerId, instagram_cost: paymentCost, instagram_currency_id: paymentCurrencyId };

        update(patchData);
    };

    const handleDismiss = () => setDismissedMismatchKey(mismatchKey);

    return (
        <Banner appearance="warning" className={cn(styles.banner, className)} displayClose onClose={handleDismiss}>
            <WarningIcon className={styles.icon} />
            <div className={styles.content}>
                <p className={styles.title}>{hasCreatorCost ? "Creator's fee has changed" : 'No creator fee set'}</p>
                <p className={styles.description}>
                    {hasCreatorCost ? (
                        <>
                            Looks like this creator&apos;s fee has changed from <strong>{creatorAmount}</strong> to{' '}
                            <strong>{paymentAmount}</strong>. Want to update it to the new figure going forward?
                        </>
                    ) : (
                        <>
                            This creator has no {platformLabel} fee set yet. Want to set it to{' '}
                            <strong>{paymentAmount}</strong> going forward?
                        </>
                    )}
                </p>
                <Button
                    appearance="primary"
                    type="button"
                    className={styles.action}
                    onClick={handleUpdate}
                    isLoading={isLoading}
                    disabled={isLoading}
                >
                    {hasCreatorCost ? `Update creator fee to ${paymentAmount}` : `Set creator fee to ${paymentAmount}`}
                </Button>
            </div>
        </Banner>
    );
};

export default CreatorCostMismatchBanner;
